/**
 * main.ts — Mathillery entry point
 *
 * Creates the Phaser game instance and registers all scenes.
 */

import * as Phaser from 'phaser';
import { MenuScene } from './scenes/MenuScene';
import { PracticeScene } from './scenes/PracticeScene';
import { MultiplayerScene } from './scenes/MultiplayerScene';
import { ReplayScene } from './scenes/ReplayScene';
import { P2PScene } from './scenes/P2PScene';
import './ui/styles.css';

// Initialize theme
const savedTheme = localStorage.getItem('mathillery_theme') || 'light';
document.documentElement.dataset.theme = savedTheme;

const config: Phaser.Types.Core.GameConfig = {
  type: Phaser.AUTO,
  parent: 'game-container',
  width: window.innerWidth,
  height: window.innerHeight,
  transparent: true,
  scene: [MenuScene, PracticeScene, MultiplayerScene, ReplayScene, P2PScene],
  scale: {
    mode: Phaser.Scale.RESIZE,
    autoCenter: Phaser.Scale.CENTER_BOTH,
  },
  render: {
    antialias: true,
    pixelArt: false,
  },
};

// Create the game
const game = new Phaser.Game(config);

// ── Browser Back Button → Menu ──────────────────────────────
// Push a "menu" state on first load so the user has something to "go back" to.
history.replaceState({ scene: 'menu' }, '');

// When a game scene starts, push a new history entry so the back button
// takes the user to the menu instead of leaving the page entirely.
game.events.on('step', () => {
  const activeScene = game.scene.getScenes(true)[0];
  if (!activeScene) return;
  const key = activeScene.scene.key;
  const histState = history.state as { scene?: string } | null;

  if (key !== 'MenuScene' && (!histState || histState.scene === 'menu')) {
    history.pushState({ scene: key }, '');
  } else if (key === 'MenuScene' && histState && histState.scene !== 'menu') {
    history.replaceState({ scene: 'menu' }, '');
  }
});

// When the user presses the browser back button, go to the menu.
window.addEventListener('popstate', () => {
  const activeScene = game.scene.getScenes(true)[0];
  if (!activeScene) return;
  const key = activeScene.scene.key;

  if (key !== 'MenuScene') {
    // Clean up any HTML overlays from the current scene
    const hud = document.querySelector('.hud-container');
    if (hud) hud.remove();
    const result = document.querySelector('.result-overlay');
    if (result) result.remove();

    // Import p2pManager to disconnect if in a P2P game
    import('./core/p2p').then(({ p2pManager }) => {
      p2pManager.disconnect();
    });

    game.scene.start('MenuScene');
    // Ensure we have a menu state in history
    history.replaceState({ scene: 'menu' }, '');
  }
});
